import {
  Box, Card, CardContent, Typography, Button, Stack, Grid, Chip, Alert, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Divider, Switch,
  FormControlLabel, CircularProgress, Tooltip,
} from '@mui/material';
import {
  Hub, CalendarMonth, TableChart, Email, Webhook as WebhookIcon, Tag, CheckCircle,
  Link as LinkIcon, PlayArrow, HelpOutline, TrendingUp, Bolt, ShoppingCart,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { api } from '../config/api';
import MercadoLibreCard, { ERRORES_MELI } from '../components/MercadoLibreCard';
import WooCommerceCard from '../components/WooCommerceCard';
import StatsIntegraciones from '../components/StatsIntegraciones';
import { EXPLICACIONES } from '../utils/integracionesExplicaciones';

const ICONS: Record<string, React.ReactNode> = {
  whatsapp: <Hub />, instagram: <Hub />, messenger: <Hub />,
  calendar: <CalendarMonth />, sheets: <TableChart />, gmail: <Email />,
  webhook: <WebhookIcon />, slack: <Tag />,
  woocommerce: <ShoppingCart />, mercadolibre: <ShoppingCart />,
  zapier: <Bolt />, n8n: <Bolt />,
};
const COLORS: Record<string, string> = {
  whatsapp: '#25D366', instagram: '#E4405F', messenger: '#0084FF',
  google_calendar: '#4285F4', google_sheets: '#0F9D58', gmail: '#EA4335',
  webhook: '#F59E0B', slack: '#611f69',
  woocommerce: '#96588A', mercadolibre: '#FFE600',
  zapier: '#FF4F00', n8n: '#FF6B6B',
};

const Integraciones: React.FC = () => {
  const [items, setItems] = useState<any[]>([]);
  const [googleOk, setGoogleOk] = useState(true);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState<{ t: string; sev: 'success' | 'error' | 'info' } | null>(null);
  const [dlg, setDlg] = useState<any>(null);
  const [dlgExplicacion, setDlgExplicacion] = useState<string | null>(null); // Modal de explicación
  const [webhookUrl, setWebhookUrl] = useState('');
  const [sheetId, setSheetId] = useState('');
  const [googleSel, setGoogleSel] = useState<Record<string, boolean>>({ google_calendar: true, google_sheets: false, gmail: false });
  const [params, setParams] = useSearchParams();
  // Modelo B: credenciales propias de Google
  const [tieneCredsPropias, setTieneCredsPropias] = useState(false);
  const [redirectUri, setRedirectUri] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');

  // Force webpack to include dlgExplicacion (prevent tree-shaking of modal state)
  useEffect(() => {
    if (dlgExplicacion) {
      // Modal is open - ensure explanation data is available
      const exp = EXPLICACIONES[dlgExplicacion];
      if (exp) console.debug('Modal abierto:', exp.titulo);
    }
  }, [dlgExplicacion]);

  const cargar = () => {
    setLoading(true);
    api.get('/integraciones').then((r) => {
      setItems(r.data.integraciones);
      setGoogleOk(r.data.googleDisponible);
      setTieneCredsPropias(r.data.tieneCredencialesPropias);
      setRedirectUri(r.data.redirectUri || '');
    }).finally(() => setLoading(false));
  };
  useEffect(cargar, []);

  useEffect(() => {
    const conectado = params.get('conectado');
    const error = params.get('error');
    if (conectado) {
      setMsg({ t: conectado === 'mercadolibre' ? '✅ Tu cuenta de Mercado Libre quedó conectada' : 'Cuenta de Google conectada correctamente', sev: 'success' });
      params.delete('conectado'); setParams(params); cargar();
    }
    if (error) {
      const t = error.startsWith('meli_') ? (ERRORES_MELI[error] || ERRORES_MELI.meli_oauth) : 'No se pudo conectar. Revisa la configuración de Google.';
      setMsg({ t, sev: 'error' }); params.delete('error'); setParams(params);
    }
  }, [params, setParams]);

  const conectarGoogle = async () => {
    const tipos = Object.keys(googleSel).filter((k) => googleSel[k]);
    try {
      const { data } = await api.post('/integraciones/google/conectar', { tipos });
      window.location.href = data.authUrl; // redirige a Google
    } catch (e: any) {
      setMsg({ t: e.response?.data?.error || 'Error al conectar Google', sev: 'error' });
    }
  };

  const guardarWebhook = async (tipo: string) => {
    await api.put('/integraciones/webhook', { tipo, webhook_url: webhookUrl });
    setDlg(null); setWebhookUrl(''); setMsg({ t: 'Integración guardada', sev: 'success' }); cargar();
  };

  const guardarSheetId = async () => {
    await api.put('/integraciones/config', { tipo: 'google_sheets', config: { sheet_id: sheetId } });
    setDlg(null); setSheetId(''); setMsg({ t: 'ID de planilla guardado', sev: 'success' }); cargar();
  };

  const desconectar = async (tipo: string) => {
    await api.delete(`/integraciones/${tipo}`); setMsg({ t: 'Integración desconectada', sev: 'info' }); cargar();
  };

  const guardarCredsPropias = async () => {
    try {
      await api.put('/integraciones/google/credenciales-propias', { client_id: clientId, client_secret: clientSecret });
      setDlg(null); setClientId(''); setClientSecret('');
      setMsg({ t: 'Credenciales propias guardadas. Ya puedes conectar tus cuentas de Google.', sev: 'success' });
      cargar();
    } catch (e: any) {
      setMsg({ t: e.response?.data?.error || 'Error guardando credenciales', sev: 'error' });
    }
  };

  const borrarCredsPropias = async () => {
    await api.delete('/integraciones/google/credenciales-propias');
    setMsg({ t: 'Credenciales propias eliminadas. Se usarán las de la plataforma.', sev: 'info' });
    cargar();
  };

  const copiar = (t: string) => { navigator.clipboard.writeText(t); setMsg({ t: 'Copiado', sev: 'info' }); };

  const probar = async (tipo: string) => {
    setMsg({ t: 'Probando...', sev: 'info' });
    try {
      const { data } = await api.post(`/integraciones/probar/${tipo}`);
      setMsg(data.ok ? { t: '✅ Prueba exitosa, revisa tu herramienta', sev: 'success' } : { t: `Error: ${data.error}`, sev: 'error' });
    } catch (e: any) {
      setMsg({ t: e.response?.data?.error || 'Error en la prueba', sev: 'error' });
    }
  };

  const grupos = Array.from(new Set(items.map((i) => i.categoria)));

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Hub color="primary" />
        <Typography variant="h4">Vende Más con IA</Typography>
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Tu agente IA atiende a tus clientes 24/7 en WhatsApp con información en vivo de tu tienda: productos, precios, stock y estado de pedidos. Aumenta ventas, reduce costos de soporte y captura leads automáticamente.
      </Typography>

      {msg && <Alert severity={msg.sev} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.t}</Alert>}

      {/* SECCIÓN: RESULTADOS MEDIBLES */}
      <StatsIntegraciones />

      {/* SECCIÓN: VENTAS - Integraciones de Ecommerce */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ShoppingCart fontSize="small" /> Ventas
        </Typography>
      </Box>

      {/* Panel de credenciales de Google: Modelo A (plataforma) + Modelo B (propias) */}
      <Card sx={{ mb: 3, border: '1px solid #E2E8F0' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
            <Avatar sx={{ bgcolor: '#4285F418', color: '#4285F4', width: 32, height: 32 }}><LinkIcon fontSize="small" /></Avatar>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Conectar con Google</Typography>
            {tieneCredsPropias && <Chip size="small" color="primary" label="Usando tus credenciales" />}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Conecta tu Google Calendar, Sheets o Gmail con un clic. Solo autorizas con tu propia cuenta de Google,
            y cada cuenta accede únicamente a su propio calendario.
          </Typography>
          {!googleOk ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              La conexión con Google no está disponible por ahora. Mientras tanto puedes usar los webhooks (Zapier/Make/Slack).
            </Alert>
          ) : (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
              <Button size="small" variant="contained" startIcon={<LinkIcon />}
                onClick={() => { setGoogleSel({ google_calendar: true, google_sheets: false, gmail: false }); setDlg({ tipo: 'google' }); }}
                sx={{ bgcolor: '#4285F4', '&:hover': { bgcolor: '#3367d6' } }}>
                Conectar con Google
              </Button>
              <Button size="small" color="inherit" sx={{ color: 'text.secondary' }} onClick={() => setDlg({ tipo: 'creds_propias' })}>
                {tieneCredsPropias ? 'Editar mis credenciales' : 'Opción avanzada'}
              </Button>
              {tieneCredsPropias && <Button size="small" color="error" onClick={borrarCredsPropias}>Volver al modo simple</Button>}
            </Stack>
          )}
        </CardContent>
      </Card>

      <MercadoLibreCard onMensaje={(t, sev) => setMsg({ t, sev })} />

      <WooCommerceCard onMensaje={(t, sev) => setMsg({ t, sev })} />

      {/* Alert contextualizado si no tiene herramientas activas */}
      {loading === false && items.length === 0 && (
        <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
          💡 <strong>Tip:</strong> Conecta tu tienda (Mercado Libre o WooCommerce) arriba en "Ventas" para que el agente 
          responda automáticamente sobre productos y pedidos. Sin conexión, deberás pegar manualmente tu catálogo en la base de conocimiento del agente.
        </Alert>
      )}

      {loading ? <Box sx={{ textAlign: 'center', mt: 6 }}><CircularProgress /></Box> : grupos.map((g) => (
        <Box key={g} sx={{ mb: 3 }}>
          <Typography variant="overline" color="text.secondary">{g}</Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {items.filter((i) => i.categoria === g).map((i) => (
              <Grid item xs={12} sm={6} md={4} key={i.tipo}>
                <Card sx={{ height: '100%', position: 'relative', opacity: i.disponible ? 1 : 0.6 }}>
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                      <Avatar 
                        sx={{ bgcolor: `${COLORS[i.tipo]}18`, color: COLORS[i.tipo], mb: 1, cursor: 'pointer' }}
                        onClick={() => setDlgExplicacion(i.tipo)}
                        title="Click para aprender cómo ayuda"
                      >
                        {ICONS[i.icon]}
                      </Avatar>
                      {i.conectada && <Chip size="small" color="success" icon={<CheckCircle />} label="Conectado" />}
                    </Stack>
                    <Typography variant="h6">{i.nombre}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 40 }}>{i.desc}</Typography>
                    {i.cuenta && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>📧 {i.cuenta}</Typography>}

                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {!i.conectada ? (
                        i.oauth ? (
                          <Button size="small" variant="contained" startIcon={<LinkIcon />} disabled={!i.disponible}
                            onClick={() => { setGoogleSel({ google_calendar: i.tipo === 'google_calendar', google_sheets: i.tipo === 'google_sheets', gmail: i.tipo === 'gmail' }); setDlg({ tipo: 'google' }); }}
                            sx={{ bgcolor: COLORS[i.tipo], '&:hover': { bgcolor: COLORS[i.tipo] } }}>
                            Conectar
                          </Button>
                        ) : (
                          <Button size="small" variant="contained" startIcon={<LinkIcon />}
                            onClick={() => setDlg({ tipo: i.tipo, webhook: true })}
                            sx={{ bgcolor: COLORS[i.tipo], '&:hover': { bgcolor: COLORS[i.tipo] } }}>
                            Conectar
                          </Button>
                        )
                      ) : (
                        <>
                          <Button size="small" variant="outlined" startIcon={<PlayArrow />} onClick={() => probar(i.tipo)}>Probar</Button>
                          {i.tipo === 'google_sheets' && <Button size="small" onClick={() => setDlg({ tipo: 'sheet_id' })}>ID planilla</Button>}
                          <Button size="small" color="error" onClick={() => desconectar(i.tipo)}>Desconectar</Button>
                        </>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      {/* Sección: Importa tu Catálogo */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUp fontSize="small" /> Catálogo
        </Typography>
      </Box>
      <Card sx={{ mb: 3, border: '2px dashed #cbd5e1', background: '#f8fafc' }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
            <Tooltip title="Carga un CSV con tus productos para que el agente tenga contexto completo sin necesidad de conectar API">
              <HelpOutline sx={{ fontSize: 18, color: '#64748b', cursor: 'help' }} />
            </Tooltip>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Importa tu Catálogo
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Carga un CSV con tus productos (nombre, descripción, precio, stock) para que el agente tenga contexto completo en WhatsApp. 
            El agente usará esta información para responder preguntas sobre tus productos.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
            <Button variant="outlined" startIcon={<TrendingUp />} disabled>
              📥 Descargar Plantilla CSV (próximamente)
            </Button>
            <Button variant="outlined" startIcon={<TrendingUp />} disabled>
              ⬆️ Cargar archivo (próximamente)
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
            💡 Tip: Los cambios de precio y stock en la tienda se sincronizarán automáticamente si tienes Mercado Libre o WooCommerce conectadas.
          </Typography>
        </CardContent>
      </Card>

      {/* Sección: Privacidad y Seguridad */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="overline" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <HelpOutline fontSize="small" /> Preguntas Frecuentes
        </Typography>
      </Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  ¿Mis datos están seguros?
                </Typography>
                <Tooltip title="Tus credenciales y datos de clientes están cifrados y protegidos">
                  <HelpOutline sx={{ fontSize: 14, color: '#64748b', cursor: 'help' }} />
                </Tooltip>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Sí. tRespondo solo accede a tus productos y pedidos públicos. No guardamos emails ni historial personal. 
                Todas las credenciales (Mercado Libre, WooCommerce, Google) se cifran en tránsito y en reposo.
              </Typography>
            </Box>
            <Divider />
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  ¿Se me cobra por búsquedas de productos?
                </Typography>
                <Tooltip title="No hay costos por API">
                  <HelpOutline sx={{ fontSize: 14, color: '#64748b', cursor: 'help' }} />
                </Tooltip>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                No. Usas tus propias credenciales de Mercado Libre o WooCommerce. tRespondo solo te cobra por el agente IA, 
                no por cada consulta. Las búsquedas de productos no tienen límites.
              </Typography>
            </Box>
            <Divider />
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  ¿Por qué el agente no responde sobre mis productos en WhatsApp?
                </Typography>
                <Tooltip title="Verifica que la integración esté activa">
                  <HelpOutline sx={{ fontSize: 14, color: '#64748b', cursor: 'help' }} />
                </Tooltip>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Revisa que hayas <strong>conectado tu tienda</strong> en la sección "Ventas" y que hayas 
                <strong> activado "Catálogo en Vivo"</strong>. Si aparece un error, verifica que tus credenciales 
                sean válidas y que tu tienda tenga productos activos.
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
      <Dialog open={dlg?.tipo === 'google'} onClose={() => setDlg(null)} fullWidth maxWidth="xs">
        <DialogTitle>Conectar con Google</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Elige qué quieres que tu agente pueda hacer. Se abrirá la ventana de Google para autorizar.
          </Typography>
          <FormControlLabel control={<Switch checked={googleSel.google_calendar} onChange={(e) => setGoogleSel({ ...googleSel, google_calendar: e.target.checked })} />} label="Google Calendar (agendar citas)" />
          <FormControlLabel control={<Switch checked={googleSel.google_sheets} onChange={(e) => setGoogleSel({ ...googleSel, google_sheets: e.target.checked })} />} label="Google Sheets (guardar leads)" />
          <FormControlLabel control={<Switch checked={googleSel.gmail} onChange={(e) => setGoogleSel({ ...googleSel, gmail: e.target.checked })} />} label="Gmail (enviar correos)" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(null)}>Cancelar</Button>
          <Button variant="contained" onClick={conectarGoogle}>Autorizar con Google</Button>
        </DialogActions>
      </Dialog>

      {/* Dialogo: webhook / slack */}
      <Dialog open={!!dlg?.webhook} onClose={() => setDlg(null)} fullWidth maxWidth="sm">
        <DialogTitle>Conectar {dlg?.tipo === 'slack' ? 'Slack' : 'Webhook'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {dlg?.tipo === 'slack'
              ? 'Pega la URL del Incoming Webhook de tu canal de Slack. Recibirás un aviso por cada lead nuevo.'
              : 'Pega la URL de tu webhook de Zapier, Make, n8n o cualquier servicio. tRespondo.cl enviará cada evento (lead nuevo, cita) a esa URL.'}
          </Typography>
          <TextField fullWidth label="URL del webhook" placeholder="https://hooks.zapier.com/..." value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)} />
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Con un webhook puedes conectar con miles de apps: Google Sheets, Notion, HubSpot, Trello, Telegram, etc.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(null)}>Cancelar</Button>
          <Button variant="contained" disabled={!webhookUrl} onClick={() => guardarWebhook(dlg.tipo)}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialogo: credenciales propias de Google (Modelo B) */}
      <Dialog open={dlg?.tipo === 'creds_propias'} onClose={() => setDlg(null)} fullWidth maxWidth="sm">
        <DialogTitle>Usar mis propias credenciales de Google</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Crea un proyecto en <strong>console.cloud.google.com</strong>, activa las APIs de Calendar/Sheets/Gmail,
            crea un "ID de cliente OAuth" tipo <strong>Aplicación web</strong> y registra este Redirect URI:
          </Alert>
          <TextField fullWidth size="small" label="Redirect URI (regístralo en Google)" value={redirectUri} sx={{ mb: 2 }}
            InputProps={{ readOnly: true, endAdornment: <Button size="small" onClick={() => copiar(redirectUri)}>Copiar</Button> }} />
          <Divider sx={{ mb: 2 }} />
          <TextField fullWidth label="Client ID" value={clientId} onChange={(e) => setClientId(e.target.value)} sx={{ mb: 2 }} placeholder="xxxxx.apps.googleusercontent.com" />
          <TextField fullWidth label="Client Secret" type="password" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="GOCSPX-..." />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(null)}>Cancelar</Button>
          <Button variant="contained" disabled={!clientId || !clientSecret} onClick={guardarCredsPropias}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialogo: ID de planilla */}
      <Dialog open={dlg?.tipo === 'sheet_id'} onClose={() => setDlg(null)} fullWidth maxWidth="sm">
        <DialogTitle>ID de la planilla de Google Sheets</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Copia el ID desde la URL de tu planilla: docs.google.com/spreadsheets/d/<strong>ESTE_ID</strong>/edit
          </Typography>
          <TextField fullWidth label="ID de la planilla" value={sheetId} onChange={(e) => setSheetId(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlg(null)}>Cancelar</Button>
          <Button variant="contained" disabled={!sheetId} onClick={guardarSheetId}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* MODAL: Explicación de cómo la integración ayuda al emprendedor */}
      <Dialog open={!!dlgExplicacion} onClose={() => setDlgExplicacion(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ pb: 1 }}>
          {dlgExplicacion && EXPLICACIONES[dlgExplicacion]?.titulo}
        </DialogTitle>
        <DialogContent>
          {dlgExplicacion && (
            <Stack spacing={1.5}>
              {EXPLICACIONES[dlgExplicacion]?.beneficios.map((beneficio, idx) => {
                // Extract emoji + first word, then the rest of the text
                const emojiMatch = beneficio.match(/^(\S+\s+)/);
                const emoji = emojiMatch ? emojiMatch[1] : '';
                const text = emojiMatch ? beneficio.substring(emojiMatch[0].length) : beneficio;
                return (
                  <Typography key={idx} variant="body2" sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <span style={{ minWidth: 24, flexShrink: 0 }}>{emoji}</span>
                    <span>{text}</span>
                  </Typography>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgExplicacion(null)}>Entendido</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Integraciones;
